import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Steps, message, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PageLink } from '@/config/pageConfig';
import {
  addOrEditLottoActivity,
  getLottoDetail,
  LottoDetailResponse,
} from '@/api/lottoActivity';
import { uploadResourcePositionImage } from '@/api/upload';
import StepBasicInfo from './StepBasicInfo';
import StepPrizeSetting from './StepPrizeSetting';
import StepRuleSetting from './StepRuleSetting';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { isPrizePoolChanged } from '@/utils/lotto';
import isEqual from 'lodash/isEqual';

const { Step } = Steps;

const CreateLottoActivity: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [form3] = Form.useForm();
  const [activePoolIndex, setActivePoolIndex] = useState(0);
  const [activePrizeTabs, setActivePrizeTabs] = useState<
    Record<number, string>
  >({});
  const [activityId, setActivityId] = useState<number | null>(null);
  const [rawPrizePools, setRawPrizePools] = useState<any[]>([]); // 缓存原始奖池数据
  const form1InitialValuesRef = useRef<any>({});
  useEffect(() => {
    if (id) {
      // 编辑场景
      getLottoDetail({ id }).then((res) => {
        const data = res.data || ({} as LottoDetailResponse);
        const timeRange = [dayjs(data.startTime), dayjs(data.endTime)];
        // 设置 form1（基础信息）
        form1.setFieldsValue({
          activityName: data.activityName,
          timeRange,
          activityRule: data.activityRule,
          activityDesc: data.activityDesc,
          disclaimer: data.disclaimer,
          remark: data.remark,
          maxWin: data.maxWinTimes,
          dailyLimit: data.dailyPartake,
          type: data.type,
        });
        form1InitialValuesRef.current = {
          activityName: data.activityName,
          timeRange,
          activityRule: data.activityRule,
          activityDesc: data.activityDesc,
          disclaimer: data.disclaimer,
          remark: data.remark,
          maxWin: data.maxWinTimes,
          dailyLimit: data.dailyPartake,
          type: data.type,
        };
        // 设置 form2（奖池和奖品）
        const pools = (data.prizePoolList || []).map((pool: any) => {
          const fullPrizeList = (pool.prizeList || []).map(
            (p: any, index: number) => ({
              ...p,
              prizeImage: p.prizeImage
                ? [
                    {
                      uid: String(index),
                      name: 'image.png',
                      status: 'done',
                      url: p.prizeImage,
                    },
                  ]
                : [],
            })
          );
          return {
            prizePoolName: pool.prizePoolName,
            gashaponNum: pool.gashaponNum,
            prizeList: fullPrizeList,
          };
        });
        setRawPrizePools(pools); // 缓存原始数据
        form2.setFieldsValue({
          lottoPools: pools.map((pool) => ({
            ...pool,
            prizeList: pool.prizeList.filter(
              (p: { prizeType: number }) => p.prizeType !== 5
            ), // 过滤“谢谢参与”
          })),
        });
        setActivityId(data.id);
      });
    } else {
      // 新增场景，初始化一个奖池两个奖品
      form2.setFieldsValue({
        lottoPools: [
          {
            prizeList: [{}, {}],
          },
        ],
      });
    }
  }, [id]);

  const next = async () => {
    try {
      if (current === 0) {
        await form1.validateFields();
      } else if (current === 1) {
        await form2.validateFields(); // 校验所有奖池和奖品
        const step1 = form1.getFieldsValue(true);
        const step2 = form2.getFieldsValue(true);
        const timeRange = step1.timeRange;
        if (!Array.isArray(timeRange) || timeRange.length !== 2) {
          message.error('请填写正确的活动时间');
          return;
        }
        // 校验step1是否改动
        const normalizeForm1 = (obj: any) => ({
          ...obj,
          timeRange: Array.isArray(obj.timeRange)
            ? obj.timeRange.map(
                (t: string | number | Date | dayjs.Dayjs | null | undefined) =>
                  dayjs(t).format('YYYY-MM-DD HH:mm:ss')
              )
            : obj.timeRange,
          remark: obj.remark || '',
        });
        const isStep1Changed = !isEqual(
          normalizeForm1(step1),
          normalizeForm1(form1InitialValuesRef.current)
        );
        const cleanPrizePools = (pools: any[]) =>
          (pools || []).map((pool) => ({
            ...pool,
            prizeList: (pool.prizeList || []).filter(
              (p: any) => p.prizeType !== 5 // 移除“谢谢参与”
            ),
          }));
        const changed = isPrizePoolChanged(
          cleanPrizePools(step2.lottoPools || []),
          cleanPrizePools(rawPrizePools || [])
        );
        // 校验是否改动
        if (id && !changed && !isStep1Changed) {
          setCurrent(current + 1);
          return;
        }
        const [startTime, endTime] = timeRange;
        // form 数据和 rawPrizePools 合并生成完整奖池列表
        const prizePoolList = (step2.lottoPools || []).map(
          (pool: any, index: number) => {
            const formPrizeList = pool.prizeList || [];
            const originalPrizeList = rawPrizePools[index]?.prizeList || [];
            // 检查当前奖池是否已有谢谢参与
            const hasThanksPrize = formPrizeList.some(
              (p: any) => p.prizeType === 5
            );
            // 优先使用当前表单的谢谢参与，否则使用原始缓存的
            const fallbackThanksPrize = originalPrizeList.find(
              (p: any) => p.prizeType === 5
            );
            const mergedPrizeList = [
              ...formPrizeList.map((item: any) => ({
                prizeType: item.prizeType,
                prizeName: item.prizeName,
                prizeImage:
                  item.prizeImage?.[0]?.response?.url ||
                  item.prizeImage?.[0]?.url ||
                  '',
                winLimit: item.winLimit,
                totalStock: item.totalStock || 0,
                winProbability: item.winProbability || 0,
                // pointsNum: item.pointsNum || 0,
              })),
              ...(!hasThanksPrize
                ? [
                    {
                      prizeType: 5,
                      prizeName: fallbackThanksPrize?.prizeName || '谢谢参与',
                      prizeImage:
                        fallbackThanksPrize?.prizeImage?.[0]?.url ||
                        'https://suppart-dev-s3.suppart.app/resourcePositionImage/5cf02a49-f4e7-4ae1-8a8a-14c1dba4d3fc.png',
                      winLimit: 9999,
                      totalStock: 0,
                      winProbability: fallbackThanksPrize?.winProbability || 0,
                    },
                  ]
                : []),
            ];
            return {
              gashaponNum: step1.type === 0 ? pool.gashaponNum : 0,
              prizePoolName: pool.prizePoolName || `奖池${index + 1}`,
              prizeList: mergedPrizeList,
            };
          }
        );
        const params = {
          id: activityId ?? undefined,
          activityName: step1.activityName,
          startTime: startTime.format('YYYY-MM-DD HH:mm:ss'),
          endTime: endTime.format('YYYY-MM-DD HH:mm:ss'),
          applicableUser: 1,
          activityRule: step1.activityRule,
          activityDesc: step1.activityDesc,
          disclaimer: step1.disclaimer,
          remark: step1.remark || '',
          maxWinTimes: step1.maxWin,
          dailyPartake: step1.dailyLimit,
          status: 0,
          type: step1.type,
          prizePoolList,
        };
        const res = await addOrEditLottoActivity(params);
        if (!res || res.status !== 200) {
          message.error(res?.message || '保存失败');
          return;
        }
        message.success('配置保存成功');
        if (res.data) {
          setActivityId(res.data); // 缓存活动ID用于后续更新
        }
      }
      if (current < 2) {
        setCurrent(current + 1);
      }
    } catch (err) {
      console.error('表单验证失败:', err);
      message.error('请完善所有必填信息');
    }
  };

  const prev = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };

  const customImageUpload = async ({ file, onSuccess, onError }: any) => {
    try {
      const isAllowedType = ['image/jpeg', 'image/png', 'image/jpg'].includes(
        file.type
      );
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isAllowedType) {
        message.error('请上传 JPG/JPEG/PNG 格式的图片');
        onError?.(new Error('文件类型不支持'));
        return;
      }
      if (!isLt10M) {
        message.error('图片大小不能超过 10MB');
        onError?.(new Error('文件过大'));
        return;
      }
      const imageDimensions = await new Promise<{
        width: number;
        height: number;
      }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.width, height: img.height });
          img.onerror = reject;
          img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { width, height } = imageDimensions;
      if (width > 260 || height > 260) {
        message.error('图片尺寸建议不超过 260×260 像素');
        onError?.(new Error('图片尺寸过大'));
        return;
      }
      const res = await uploadResourcePositionImage(file);
      const url = res?.data;
      if (!url) throw new Error('无效的返回结果');
      file.url = url;
      onSuccess?.({ url });
    } catch (error) {
      console.error('上传出错:', error);
      message.error('上传失败，请重试');
      onError?.(error);
    }
  };

  return (
    <div style={{ background: '#fff', padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20 }}>新增活动</h2>
        <Space>
          <Button onClick={() => navigate(PageLink.LotteryActivity)}>
            取消
          </Button>
        </Space>
      </div>

      <Steps current={current} style={{ marginBottom: 32 }}>
        <Step title="基本信息" />
        <Step title="奖品设置" />
        <Step title="规则设置" />
      </Steps>

      {current === 0 && <StepBasicInfo form={form1} onNext={next} />}

      {current === 1 && (
        <StepPrizeSetting
          form1={form1}
          form2={form2}
          activePoolIndex={activePoolIndex}
          setActivePoolIndex={setActivePoolIndex}
          activePrizeTabs={activePrizeTabs}
          setActivePrizeTabs={setActivePrizeTabs}
          next={next}
          prev={prev}
          customImageUpload={customImageUpload}
        />
      )}

      {current === 2 && (
        <StepRuleSetting
          form3={form3}
          prev={() => setCurrent(current - 1)}
          activityId={activityId}
        />
      )}
    </div>
  );
};

export default CreateLottoActivity;
